import { Observable, from, of, switchMap } from 'rxjs';
import { Injectable } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { SecureHttp } from '@app/security/secure-http';
import { environment } from '@env/environment';
import { Journey } from '@app/core/models/journey';
import { TemplateExperience } from '@app/core/models/template-experience';
import { CopyExperienceLibrary } from '@app/core/models/copy-experience-library';
import { Filter, SearchCriteria } from '../utils/search-criteria';
import { Ordering } from '../utils/ordering';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class ExperienceLibraryService {
  portalURLBase: string = environment.portalURLBase;
  constructor(private secureHttp: SecureHttp, private rawHttp: HttpClient) {}

  /**
   * Retrieve template experiences for the given client.
   *
   * @param clientId the client requesting the templates
   * @param limit
   * @param offset
   * @param searchCriteria
   * @param ordering
   * @param hideMask
   * @returns the template experiences
   */
  getExperiences(
    clientId: string,
    limit: number,
    offset: number,
    searchCriteria: SearchCriteria,
    ordering?: Ordering,
    hideMask: boolean = true,
  ): Observable<TemplateExperience> {
    if (!searchCriteria) {
      searchCriteria = new SearchCriteria();
    }
    searchCriteria.filtersIfFieldExists.push(new Filter('deleted', 'false'));

    let url = `${environment.portalURLBase}/client/${clientId}/templates?limit=${limit}&offset=${offset}`;
    if (ordering) {
      url = `${url}&${ordering.getQueryStringUnderscorish()}`;
    }
    const searchRequest = searchCriteria.getSearchRequest();
    searchRequest['include_draft'] = false;

    return this.secureHttp
      .post(url, searchRequest, null, hideMask)
      .pipe(take(1));
  }

  postCopyExperience(
    clientId: string,
    experience: Journey,
  ): Observable<{ data: string }> {
    const url = `${this.portalURLBase}/client/${clientId}/transport/copy`;
    return this.secureHttp.post(
      url,
      this.buildCopyExperienceLibrary(clientId, experience),
    );
  }

  getExperienceExport(clientId: string, experienceId: string): Observable<any> {
    const baseUrl = this.portalURLBase;
    const url = `${baseUrl}/client/${clientId}/transport/${experienceId}/presigned-url`;
    return this.rawHttp.get(url);
  }

  /**
   * Initiates the import process and uploads the exported experience to S3 using a presigned URL.
   * @param clientId
   * @param exportedExperienceFile The original File object (zip)
   * @param checksum
   * @returns Observable<{ importId: string }>
   */
  importExperience(
    clientId: string,
    exportedExperienceFile: File,
  ): Observable<any> {
    const initiateUrl = `${this.portalURLBase}/client/${clientId}/transport/initiate`;
    return this.secureHttp.post(initiateUrl, {}, {}, true).pipe(
      switchMap(
        (initResp: {
          importId: string;
          uploadUrl: string;
          bucket: string;
          key: string;
        }) => {
          return new Observable<{ importId: string }>((observer) => {
            const xhr = new XMLHttpRequest();
            xhr.withCredentials = true;
            xhr.open('PUT', initResp.uploadUrl, true);
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                observer.next({ importId: initResp.importId });
                observer.complete();
              } else {
                observer.error({
                  message: `Failed to upload file: ${xhr.status} ${xhr.statusText}`,
                  status: xhr.status,
                  statusText: xhr.statusText,
                  response: xhr.response,
                });
              }
            };
            xhr.onerror = () => {
              observer.error({
                message: 'An error occurred during the file upload.',
                status: xhr.status,
                statusText: xhr.statusText,
                response: xhr.response,
              });
            };
            xhr.send(exportedExperienceFile);
          });
        },
      ),
    );
  }

  getImportStatus(clientId: string, importId: string): Observable<any> {
    const url = `${this.portalURLBase}/client/${clientId}/transport/status/${importId}`;
    return this.secureHttp.get(url, {}, true);
  }

  buildCopyExperienceLibrary(
    clientId: string,
    experience: Journey,
  ): CopyExperienceLibrary {
    return {
      target: {
        clientId: clientId,
      },
      source: {
        clientId: experience.client_id,
        experienceId: experience.id,
      },
    };
  }
}
